import React from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@core-ui/Components/ui/collapsible.tsx';
import { Card, CardContent, CardHeader } from '@core-ui/Components/ui/card.tsx';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { type Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import IntegrationJobsTable from '@features/dashboard/components/dashboardIntegrations/IntegrationJobsTable.tsx';
import type { IntegrationJob } from '@features/dashboard/types/dashboard.types.ts';

interface Props {
  name: Integrations;
  code: string;
  icon: string;
  connected: boolean;
  isSuperAdmin: boolean;
  isTendrilSelected: boolean;
  jobs: IntegrationJob[] | null;
  isLoading: boolean;
  isRetrying: boolean;
  currentPage: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetryJob: (jobId: string) => Promise<void>;
}

export default function OutreachJobsCard ({ 
  name, 
  code, 
  icon, 
  connected,
  isSuperAdmin,
  isTendrilSelected,
  jobs,
  isLoading,
  isRetrying,
  currentPage,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  open,
  onOpenChange,
  onRetryJob,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const renderConnectionStatus = (connectionStatus: boolean):string => {
    return connectionStatus
      ? t('settings.appTab.cardStatus.connected')
      : t('settings.appTab.cardStatus.notConnected');
  };
  return (
    <Collapsible 
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          onPageChange(1);
        }
        onOpenChange(isOpen);
      }}
    >
      <Card className="gap-1 rounded-2xl shadow-lg border border-border bg-card text-foreground p-6">
        <CardHeader className="items-start w-full p-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-indigo-500/10 border border-indigo-500/30 rounded-sm">
                <img src={icon} alt={`${code}-icon`} className="w-7 h-7 object-contain" />
              </div>
              <div className="flex flex-col md:flex-row items-center justify-center shrink-0">
                <span className="text-base font-semibold leading-tight pr-3">{name}</span>
                {(!isSuperAdmin || isTendrilSelected) && (
                  <Badge
                    className={`flex h-2 text-xs p-2 items-center rounded-sm font-semibold border ${
                      connected
                        ? 'border-[var(--success-badge-border)] bg-[var(--success-badge)] text-[var(--success-text)]'
                        : 'border-border bg-muted text-muted-foreground'
                    }`}
                  >
                    {renderConnectionStatus(connected)}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-end">
              <p className="text-xs">{`${totalCount} ${t('dashboard.integrations.jobs')}`}</p>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="items-end">
                  <IconChevronDown className="size-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="grid px-0">
            <IntegrationJobsTable
              jobs={jobs}
              isLoading={isLoading}
              isRetrying={isRetrying}
              totalCount={totalCount}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              onRetryJob={onRetryJob}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}